import React, { useState, useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';

/**
 * Galerie « le jeu en vrai » — photos du produit réel (et non l'illustration).
 * Objectif : que l'acheteur voie exactement ce qu'il reçoit avant de commander.
 *
 * Les images vivent dans public/photos/ en deux tailles (600 px pour mobile,
 * 1200 px au-delà) et sont chargées en différé : aucun impact sur la vitesse
 * d'affichage de la page.
 */

export default function PhotoGallery() {
  const [zoom, setZoom] = useState(null);
  const { t } = useLang();

  // Le tableau vivait au niveau du module ; il descend ici car ses textes
  // passent par t (un hook).
  const PHOTOS = [
    {
      src: 'boite-recto',
      title: t('shop.galerie.boiteRectoTitre'),
      caption: t('shop.galerie.boiteRectoLegende'),
      alt: t('shop.galerie.boiteRectoAlt'),
    },
    {
      src: 'contenu',
      title: t('shop.galerie.contenuTitre'),
      caption: t('shop.galerie.contenuLegende'),
      alt: t('shop.galerie.contenuAlt'),
    },
    {
      src: 'pochons',
      title: t('shop.galerie.pochonsTitre'),
      caption: t('shop.galerie.pochonsLegende'),
      alt: t('shop.galerie.pochonsAlt'),
    },
    {
      src: 'boite-verso',
      title: t('shop.galerie.boiteVersoTitre'),
      caption: t('shop.galerie.boiteVersoLegende'),
      alt: t('shop.galerie.boiteVersoAlt'),
    },
  ];

  // Échap ferme la vue agrandie ; on bloque le défilement du fond.
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setZoom(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [zoom]);

  return (
    <section className="py-16 px-5 bg-white border-t-2 border-[#4AA294]/20">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-10">
          <h2 className="m-0 mb-3 text-2xl sm:text-3xl font-black text-[#33403B] uppercase tracking-tight">
            {t('shop.galerie.titre')}
          </h2>
          <p className="m-0 text-base text-[#33403B]/80 leading-relaxed">
            {t('shop.galerie.sousTitre')}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {PHOTOS.map((p) => (
            <figure key={p.src} className="m-0">
              <button
                type="button"
                onClick={() => setZoom(p)}
                data-cta={`shop:photo-${p.src}`}
                aria-label={t('shop.galerie.agrandir', { titre: p.title })}
                className="group block w-full overflow-hidden rounded-xl border-2 border-[#4AA294]/30 bg-[#F7F4EE] cursor-zoom-in transition-all duration-300 hover:border-[#4AA294] hover:shadow-[0_10px_30px_rgba(47,126,114,0.18)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
              >
                <img
                  src={`/photos/${p.src}.webp`}
                  srcSet={`/photos/${p.src}-600.webp 600w, /photos/${p.src}.webp 1200w`}
                  sizes="(max-width: 1024px) 45vw, 260px"
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[240px] sm:h-[220px] object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </button>
              <figcaption className="mt-3 px-1">
                <span className="block text-sm font-bold text-[#2F7E72]">{p.title}</span>
                <span className="block text-[13px] leading-snug text-[#33403B]/75">{p.caption}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label={zoom.title}
        >
          <figure className="m-0 max-w-[900px] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={`/photos/${zoom.src}.webp`}
              alt={zoom.alt}
              className="w-full max-h-[78vh] object-contain rounded-xl bg-white"
            />
            <figcaption className="mt-3 text-center text-sm text-white">
              <strong>{zoom.title}</strong> — {zoom.caption}
            </figcaption>
          </figure>
          <button
            type="button"
            onClick={() => setZoom(null)}
            aria-label={t('shop.galerie.fermer')}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/90 text-[#33403B] text-xl font-bold shadow-lg hover:bg-white"
          >
            ✕
          </button>
        </div>
      )}
    </section>
  );
}
